import { MaybeUndef, Undef } from 'types/UtilityTypes';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';

/**
 * Maps timezone abbreviations to IANA timezone identifiers.
 */
const TIMEZONE_ABBREVIATION_MAP: Record<string, string> = {
  // Pacific Time
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  PT: 'America/Los_Angeles',

  // Mountain Time
  MST: 'America/Denver',
  MDT: 'America/Denver',
  MT: 'America/Denver',

  // Central Time
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  CT: 'America/Chicago',

  // Eastern Time
  EST: 'America/New_York',
  EDT: 'America/New_York',
  ET: 'America/New_York',

  // Atlantic Time
  AST: 'America/Halifax',
  ADT: 'America/Halifax',
  AT: 'America/Halifax',
};

function isValidIANATimezone(timezone: string): boolean {
  try {
    // Try to create a DateTimeFormat with the timezone
    // This will throw if the timezone is invalid
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Normalizes a timezone string to IANA format.
 * If the input is already in IANA format, returns it as-is.
 * If the input is a timezone abbreviation (e.g., "PST", "EDT"), converts it to IANA format.
 * Also handles malformed timezone strings like "AmericaChicago" -> "America/Chicago"
 *
 * @param timezone - The timezone string to normalize (e.g., "PST", "America/Chicago", "AmericaChicago")
 * @returns The IANA timezone identifier, or undefined if the timezone cannot be normalized
 *
 * @example
 * normalizeTimezone('PST') // returns 'America/Los_Angeles'
 * normalizeTimezone('America/Chicago') // returns 'America/Chicago'
 * normalizeTimezone('AmericaChicago') // returns 'America/Chicago'
 * normalizeTimezone('AmericaLosAngeles') // returns 'America/Los_Angeles'
 * normalizeTimezone('EDT') // returns 'America/New_York'
 * normalizeTimezone('invalid') // returns undefined
 */
export function normalizeTimezone(timezone: MaybeUndef<string>): Undef<string> {
  if (!timezone) {
    return undefined;
  }

  const trimmedTimezone = timezone.trim();

  if (isValidIANATimezone(trimmedTimezone)) {
    return trimmedTimezone;
  }

  // Handle malformed timezone strings like "AmericaChicago" -> "America/Chicago"
  // Pattern: "America" followed by a city name without slash
  const malformedPattern = /^America([A-Z][a-zA-Z]+)$/;
  const malformedTimezoneMatch = trimmedTimezone.match(malformedPattern);
  if (malformedTimezoneMatch) {
    const malformedCityName = malformedTimezoneMatch[1];
    // Common city name mappings for malformed timezones
    const cityMappings: Record<string, string> = {
      Chicago: 'America/Chicago',
      LosAngeles: 'America/Los_Angeles',
      NewYork: 'America/New_York',
      Denver: 'America/Denver',
      Phoenix: 'America/Phoenix',
      Anchorage: 'America/Anchorage',
      Honolulu: 'Pacific/Honolulu',
      Toronto: 'America/Toronto',
      Vancouver: 'America/Vancouver',
      MexicoCity: 'America/Mexico_City',
      SaoPaulo: 'America/Sao_Paulo',
      BuenosAires: 'America/Buenos_Aires',
    };

    if (cityMappings[malformedCityName]) {
      const normalized = cityMappings[malformedCityName];
      // Verify the normalized timezone is valid
      if (isValidIANATimezone(normalized)) {
        return normalized;
      }
    }

    // Try to construct a valid IANA timezone by inserting slash and underscores
    // For example: "AmericaLosAngeles" -> "America/Los_Angeles"
    // This handles common patterns where city names have capital letters
    const parts = malformedCityName.match(/[A-Z][a-z]+/g);
    if (parts && parts.length > 0) {
      // Insert underscores between words and try common IANA formats
      const cityWithUnderscores = parts.join('_');
      const candidate = `America/${cityWithUnderscores}`;
      if (isValidIANATimezone(candidate)) {
        return candidate;
      }
    }
  }

  const upperTimezone = trimmedTimezone.toUpperCase();
  const mappedTimezone = TIMEZONE_ABBREVIATION_MAP[upperTimezone];

  if (mappedTimezone) {
    return mappedTimezone;
  }

  return undefined;
}

/** Go/protobuf "empty" UTC datetimes from the API (must not be shown in DatePicker). */
const GO_ZERO_UTC_DATETIME_SENTINEL = '0001-01-01T00:00:00Z';
const GO_ZERO_UTC_DATETIME_SENTINEL_MS = '0001-01-01T00:00:00.000Z';

function isGoZeroUtcString(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  const t = value.trim();
  return (
    t === '' ||
    t === GO_ZERO_UTC_DATETIME_SENTINEL ||
    t === GO_ZERO_UTC_DATETIME_SENTINEL_MS
  );
}

const DATE_TIME_FIELDS = new Set([
  'apptStartTime',
  'apptEndTime',
  'readyTime',
  'mustDeliver',
  'openTime',
  'closeTime',
]);

/**
 * Replaces Go zero-datetime strings and empty strings with `null` on datetime fields of the stop object
 * (pickup / consignee slice). Only string values matching the sentinel or empty string are changed.
 */
function nullGoZeroUtcFields<T extends Record<string, unknown>>(
  loc: MaybeUndef<T>
): MaybeUndef<T> {
  if (!loc) {
    return loc;
  }

  let objectWasTouched = false;
  const draftLoc = { ...loc } as Record<string, unknown>;

  for (const key of Object.keys(draftLoc)) {
    if (DATE_TIME_FIELDS.has(key)) {
      const value = draftLoc[key];
      if (value != null && isGoZeroUtcString(value)) {
        draftLoc[key] = null;
        objectWasTouched = true;
      }
    }
  }

  return (objectWasTouched ? (draftLoc as T) : loc) as MaybeUndef<T>;
}

/**
 * Normalizes timezones in load building suggestions.
 * Timezone abbreviations (e.g., "CDT", "PST") are converted to IANA format (e.g., "America/Chicago").
 * Go zero datetimes on pickup/consignee (`0001-01-01T00:00:00Z` / `.000Z`) are cleared to `null`
 * so empty appointments are not shown as 01/01/0001 in the form.
 *
 * @param suggestions - Array of raw load building suggestions from the API
 * @returns Array of suggestions with normalized timezones
 */
export function normalizeSuggestions(
  suggestions: LoadBuildingSuggestions[]
): LoadBuildingSuggestions[] {
  return suggestions.map((suggestion) => {
    const suggested = suggestion.suggested as SuggestedLoad;

    return {
      ...suggestion,
      suggested: {
        ...suggested,
        pickup: suggested.pickup
          ? {
              ...nullGoZeroUtcFields({
                ...suggested.pickup,
              } as Record<string, unknown>),
              timezone: normalizeTimezone(suggested.pickup.timezone) || '',
            }
          : suggested.pickup,
        consignee: suggested.consignee
          ? {
              ...nullGoZeroUtcFields({
                ...suggested.consignee,
              } as Record<string, unknown>),
              timezone: normalizeTimezone(suggested.consignee.timezone) || '',
            }
          : suggested.consignee,
      } as SuggestedLoad,
    };
  });
}
